/**
 * Turn a FastAPI error body into something a human can read.
 *
 * `detail` is not always a string. Our own gates raise it as an object so the
 * frontend can branch on a stable code (e.g. the Training Hub module gate sends
 * `{code, module, message}`), and FastAPI's own validation errors raise it as a
 * list. Passing either straight into `new Error(...)` yields "[object Object]",
 * which is what users were being shown.
 */

interface ApiErrorDetail {
  code?: string
  message?: string
}

export function apiErrorMessage(body: unknown, fallback: string): string {
  if (!body || typeof body !== 'object') return fallback

  const detail = (body as { detail?: unknown }).detail

  if (typeof detail === 'string' && detail.trim()) return detail

  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const { message } = detail as ApiErrorDetail
    if (typeof message === 'string' && message.trim()) return message
  }

  return fallback
}

/** The stable error code a gate sent, if any — for branching, not for display. */
export function apiErrorCode(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null
  const detail = (body as { detail?: unknown }).detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const { code } = detail as ApiErrorDetail
    if (typeof code === 'string') return code
  }
  return null
}
